import React from "react";
import { ArrowUpRight, FileText } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type { TreatmentOverviewItem } from "@/types/treatment-overview";

interface TreatmentOverviewTableProps {
  data: TreatmentOverviewItem[];
  isLoading: boolean;
  isConsultationFlow: boolean;
  isError: boolean;
  errorMessage?: string;
  withCard?: boolean;
  canViewNotes: (providerName: string) => boolean;
  onViewNotes: (item: TreatmentOverviewItem) => void;
  onViewPdf: (pdfUrl: string, item?: TreatmentOverviewItem) => void;
}

const HEADER_CLASS = "z-label uppercase tracking-wide text-muted-foreground";

const LoadingState = () => (
  <Table>
    <TableHeader>
      <TableRow className="bg-card hover:bg-transparent">
        <TableHead className={HEADER_CLASS}>Date</TableHead>
        <TableHead className={HEADER_CLASS}>Provider</TableHead>
        <TableHead className={HEADER_CLASS}>Advice</TableHead>
        <TableHead className={cn(HEADER_CLASS, "hidden lg:table-cell")}>
          Program
        </TableHead>
        <TableHead className="w-[120px]" />
      </TableRow>
    </TableHeader>
    <TableBody>
      {Array.from({ length: 6 }).map((_, index) => (
        <TableRow key={index}>
          <TableCell className="py-3">
            <Skeleton className="h-4 w-20" />
          </TableCell>
          <TableCell className="py-3">
            <div className="space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-20" />
            </div>
          </TableCell>
          <TableCell className="py-3">
            <div className="space-y-2">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-3 w-16" />
            </div>
          </TableCell>
          <TableCell className="hidden lg:table-cell py-3">
            <Skeleton className="h-4 w-24" />
          </TableCell>
          <TableCell className="py-3">
            <Skeleton className="ml-auto h-9 w-24 rounded-xl" />
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

export const TreatmentOverviewTable = ({
  data,
  isLoading,
  isError,
  errorMessage,
  withCard = true,
  onViewPdf,
}: TreatmentOverviewTableProps) => {
  const shell = (children: React.ReactNode) => (
    <div
      className={cn(
        "flex min-h-0 flex-1 flex-col overflow-hidden",
        withCard && "rounded-3xl border border-border/60 bg-card shadow-sm"
      )}
    >
      {children}
    </div>
  );

  if (isLoading) {
    return shell(
      <div className="min-h-0 flex-1 overflow-auto">
        <LoadingState />
      </div>
    );
  }

  if (isError) {
    return shell(
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <FileText className="h-5 w-5" />
        </div>
        <div className="space-y-1">
          <h2 className="z-h3 text-foreground">Unable to load records</h2>
          <p className="z-secondary text-muted-foreground">
            {errorMessage || "Failed to load treatment data."}
          </p>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return shell(
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <FileText className="h-5 w-5" />
        </div>
        <div className="space-y-1">
          <h2 className="z-h3 text-foreground">No records found</h2>
          <p className="max-w-sm z-secondary text-muted-foreground">
            Treatment records will appear here once available.
          </p>
        </div>
      </div>
    );
  }

  return shell(
    <div className="min-h-0 flex-1 overflow-auto">
      <Table>
        <TableHeader className="sticky top-0 z-10 bg-card">
          <TableRow className="hover:bg-card">
            <TableHead className={cn(HEADER_CLASS, "min-w-[120px]")}>
              Date
            </TableHead>
            <TableHead className={cn(HEADER_CLASS, "min-w-[200px]")}>
              Provider
            </TableHead>
            <TableHead className={cn(HEADER_CLASS, "min-w-[200px]")}>
              Advice
            </TableHead>
            <TableHead
              className={cn(HEADER_CLASS, "hidden lg:table-cell min-w-[160px]")}
            >
              Program
            </TableHead>
            <TableHead className={cn(HEADER_CLASS, "w-[140px] text-right")}>
              Actions
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((item, index) => (
            <TableRow
              key={`${item.appointment_id}-${index}`}
              className="bg-card transition-colors hover:bg-muted/20"
            >
              <TableCell className="py-3 z-body tabular-nums text-foreground">
                {item.date}
              </TableCell>

              <TableCell className="py-3">
                <div className="min-w-0 space-y-1">
                  <p className="truncate z-body-md text-foreground">
                    {item.provider}
                  </p>
                  {item.provider_type ? (
                    <Badge
                      variant="outline"
                      className="rounded-full border-border/70 bg-background/80 px-2.5 py-0.5 z-caption font-normal text-muted-foreground shadow-none"
                    >
                      <span className="truncate">{item.provider_type}</span>
                    </Badge>
                  ) : null}
                </div>
              </TableCell>

              <TableCell className="py-3">
                <div className="min-w-0 space-y-1">
                  <p className="truncate z-body text-foreground">{item.type}</p>
                  {item.advice ? (
                    <Badge
                      variant="outline"
                      className="rounded-full border-border/70 bg-background/80 px-2.5 py-0.5 z-caption z-numeric font-normal text-muted-foreground shadow-none"
                    >
                      <span className="truncate">{item.advice}</span>
                    </Badge>
                  ) : null}
                </div>
              </TableCell>

              <TableCell className="hidden lg:table-cell py-3 z-body text-foreground">
                {item.program || (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>

              <TableCell className="py-3">
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onViewPdf(item.pdf_url, item)}
                    disabled={!item.pdf_url}
                    className="h-9 gap-1.5 rounded-xl border-border/70 bg-background/80 px-3 shadow-none hover:bg-accent/20"
                  >
                    Advice
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
